import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ApiError, api } from "@/lib/api";
import { usePaywall } from "@/features/paywall";
import { classifyGenerationError } from "@/features/menu/generationErrors";
import { useMenu } from "@/features/menu/store";
import { MENU_LATEST_KEY } from "@/hooks/useMenuPlan";
import { MENU_NEXT_KEY } from "@/hooks/useNextWeekMenuPlan";
import type {
  MenuPlan,
  RegenerateMealRequest,
  ValidatedMenu,
} from "@/types/menu";

export type SwapTarget = "current" | "next";

/**
 * Premium single-dinner swap (audit C1).
 *
 * The backend regenerates only the targeted dinner and returns a *draft*
 * plan. This hook:
 *   - replaces the plan in the store WITHOUT dropping the persisted
 *     `menuId` / `nextWeekMenuId`, per-recipe progress or shopping ticks
 *     (only the batch cooking plan is recomputed, so its cursor resets);
 *   - never persists the result by itself — `hasPendingSwap` flags the
 *     unsaved change and `save()` is the explicit validation (BRD §5).
 */
export function useMealSwap(target: SwapTarget) {
  const { t } = useTranslation();
  const paywall = usePaywall();
  const queryClient = useQueryClient();

  const plan = useMenu((s) => (target === "current" ? s.plan : s.nextWeekPlan));
  const menuId = useMenu((s) =>
    target === "current" ? s.menuId : s.nextWeekMenuId,
  );
  const swapFlag = useMenu((s) =>
    target === "current" ? s.pendingSwap : s.nextWeekPendingSwap,
  );

  const swapMutation = useMutation({
    mutationFn: (req: RegenerateMealRequest) =>
      api<MenuPlan>("/api/v1/menu/regenerate-meal", {
        method: "POST",
        body: req,
      }),
    onSuccess: (fresh) => {
      if (target === "current") {
        useMenu.setState({
          plan: fresh,
          pendingSwap: true,
          cookingIndex: 0,
          doneSteps: [],
        });
      } else {
        useMenu.setState({ nextWeekPlan: fresh, nextWeekPendingSwap: true });
      }
      toast.success(t("menuSwap.swapped"));
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 402) {
        paywall.open({ feature: t("menuSwap.premiumFeature") });
      } else if (err instanceof ApiError && err.status === 502) {
        toast.error(t("menuSwap.failed"));
      } else if (err instanceof ApiError) {
        // Daily regen cap (429 regen_limit_reached), per-minute limiter,
        // ledger outage (503), draft binding (403), no household (409):
        // the same French messages as the generation flows, never the raw
        // "API error 429".
        toast.error(classifyGenerationError(err, t).message);
      } else {
        toast.error((err as Error).message);
      }
    },
  });

  const saveMutation = useMutation({
    mutationFn: (toSave: MenuPlan) =>
      api<ValidatedMenu>("/api/v1/menu/validate", {
        method: "POST",
        body: toSave,
      }),
    onSuccess: (res) => {
      const id = res.menu_id ?? null;
      if (target === "current") {
        // Keep cooking progress / shopping ticks: same week, same menu row.
        useMenu.setState({ plan: res.plan, menuId: id, pendingSwap: false });
        queryClient.setQueryData(MENU_LATEST_KEY, res);
      } else {
        useMenu.setState({
          nextWeekPlan: res.plan,
          nextWeekMenuId: id,
          nextWeekPendingSwap: false,
        });
        queryClient.setQueryData(MENU_NEXT_KEY, res);
      }
      toast.success(
        res.persisted ? t("menuSwap.saved") : t("menuSwap.savedLocal"),
      );
    },
    onError: (err) => {
      toast.error((err as Error).message);
    },
  });

  // A swapped draft awaiting explicit confirmation: flagged in the store by
  // the swap itself (works without a menu id, e.g. in dev), or — for plans
  // swapped before the flag existed — a draft that still carries the id of
  // the persisted menu. Next-week drafts only ever come from a swap (the
  // reveal flow writes the main store).
  const hasPendingSwap =
    !!plan &&
    plan.status !== "validated" &&
    (swapFlag || target === "next" || menuId !== null);

  function swap(day: string, slug: string) {
    if (!plan) return;
    swapMutation.mutate({ plan, day, slot: "dinner", avoid_slug: slug });
  }

  function save() {
    if (!plan) return;
    saveMutation.mutate(plan);
  }

  return {
    swap,
    isSwapping: swapMutation.isPending,
    save,
    isSaving: saveMutation.isPending,
    hasPendingSwap,
  };
}
